import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom'; 
import { 
  Shield, LayoutDashboard, ShieldAlert, ScrollText, Users, 
  LogOut, Eye, Server, Wifi, Activity, Download, RefreshCw, X, Clock, 
  CheckCircle, Settings, UserCircle, Briefcase, Filter, Sparkles, ChevronDown, CreditCard, BrainCircuit   
} from 'lucide-react';
import SubscriptionGuard from './SubscriptionGuard'; 
import logo from '../img/image-2026-05-21-08-52-40-636.png';

const LogsPage = ({ onLogout }) => {
  const [data, setData] = useState({subscription_status: 'loading', agents_online: 0, uptime: '...', alerts: 0 });
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [selectedLog, setSelectedLog] = useState(null);
  const [users, setUsers] = useState([]); 
  const [loading, setLoading] = useState(false);
  const [agents, setAgents] = useState([]);
  

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [activeFilter, setActiveFilter] = useState('All'); 
  const [hostFilter, setHostFilter] = useState('All Hosts'); 
  const [showOnlyMine, setShowOnlyMine] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  const userRole = localStorage.getItem('role');
  const currentUser = localStorage.getItem('user');
  const tenantId = localStorage.getItem('tenant_id');

  const fetchData = async (page = 1) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { 'X-NOC-Token': token } };
      
 
      const [s, l, u, a] = await Promise.all([
        axios.get('https://ai-noc.skynix.co/api/stats', config),
        axios.get(`https://ai-noc.skynix.co/api/logs?page=${page}&limit=100`, config),
        axios.get('https://ai-noc.skynix.co/api/users', config).catch(() => ({ data: [] })),
        axios.get('https://ai-noc.skynix.co/api/agents', config)
      ]);
      
      setData(s.data);
      setLogs(l.data.items || []); 
      setTotalPages(l.data.pages || 1);
      setCurrentPage(l.data.page || 1);
      setUsers(u.data || []);
      setAgents(a.data);

      } catch (e) { if (e.response?.status === 401) onLogout(); }
    setLoading(false);
  };



   useEffect(() => {
  const lookupId = location.state?.autoOpenId;
  const token = localStorage.getItem('token');

  if (lookupId) {
    axios.get(`https://ai-noc.skynix.co/api/standalone-log/${lookupId}`, {
      headers: { 'X-NOC-Token': token }
    })
    .then(res => {
      const targetPage = res.data.page;
      setCurrentPage(targetPage);
      fetchData(targetPage); 
      setSelectedLog(res.data.log); 

      
      setTimeout(() => {
        const element = document.getElementById(`row-${res.data.log.id}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 600);

      window.history.replaceState({}, document.title);
    })
    .catch(err => {
      console.error("Deep link fail:", err);
      fetchData(1);
    });
  } else {
    fetchData(currentPage);
  }
}, [location.state]);


  useEffect(() => {
    let result = [...logs];
    if (activeFilter !== 'All') {
      const sevMap = { 'Error': 1, 'Warning': 2, 'Info': 3 };
      result = result.filter(i => i.severity === sevMap[activeFilter]);
    }
    if (hostFilter !== 'All Hosts') result = result.filter(i => i.host === hostFilter);
    if (showOnlyMine) result = result.filter(i => i.assignee === currentUser);
    setFilteredLogs(result);
  }, [logs, activeFilter, hostFilter, showOnlyMine, currentUser]);


  const uniqueHosts = useMemo(() => {
    const hostsFromAgents = agents.map(a => a.host);
    const hostsFromLogs = logs.map(l => l.host);
    return ['All Hosts', ...new Set([...hostsFromAgents, ...hostsFromLogs])].filter(Boolean);
  }, [agents, logs]);

  const handleExport = () => {
    const csvRows = [
      ["ID", "Time", "Host", "Type", "Severity", "Status", "Assignee"],
      ...filteredLogs.map(log => [
        `RAW-${log.display_id || log.id}`,
        log.last_seen,
        log.host,
        log.type,
        log.severity === 1 ? 'CRITICAL' : log.severity === 2 ? 'WARNING' : 'INFO',
        log.status,
        log.assignee || 'Unassigned'
      ])
    ];
    const csvContent = "data:text/csv;charset=utf-8," + csvRows.map(e => e.join(",")).join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `logs_export_${new Date().getTime()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="d-flex" style={{minHeight: '100vh', backgroundColor: '#0b0e14', color: '#e2e8f0'}}>
       <SubscriptionGuard data={data} onLogout={onLogout} tenantId={tenantId}>
      
      <div className="sidebar" style={{ width: '64px', background: '#0d1117', borderRight: '1px solid #1e293b', height: '100vh', position: 'fixed', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '25px 0', zIndex: 1050 }}>
              <div
                        className="sidebar-item d-flex justify-content-center"
                        data-label="Go to Landing Page" 
                        onClick={() => window.location.href = 'https://noc.skynix.co'}
                        style={{
                          transform: 'translateY(-6px)',
                          marginBottom: '20px',
                          cursor: 'pointer' 
                        }}
                      >
                        <img
                          src={logo}
                          alt="SKYNIX"
                          style={{
                            width: '34px',
                            height: '34px',
                            objectFit: 'contain'
                          }}
                        />
                      </div>
        <div className="sidebar-item" data-label="Dashboard" onClick={() => navigate('/')} style={{ color: '#94a3b8', cursor: 'pointer', marginBottom: '30px' }}><LayoutDashboard size={24} /></div>
        <div className="sidebar-item" data-label="Security Incidents" onClick={() => navigate('/incidents')} style={{ color: '#94a3b8', cursor: 'pointer', marginBottom: '30px' }}><ShieldAlert size={24} /></div>
        <div className="sidebar-item active" data-label="System Logs" style={{ background: 'rgba(0, 242, 255, 0.1)', color: '#00f2ff', padding: '10px', borderRadius: '8px', cursor: 'pointer', marginBottom: '30px' }}><ScrollText size={24} /></div>
        <div className="sidebar-item" data-label="Infrastructure" onClick={() => navigate('/infrastructure')} style={{ color: '#94a3b8', cursor: 'pointer', marginBottom: '30px' }}><Server size={24} /></div>
      
        <div className="sidebar-item" data-label="User Management" onClick={() => navigate('/users')} style={{ color: '#94a3b8', cursor: 'pointer', marginBottom: '30px' }}><Users size={24} /></div>
        
        <div className="sidebar-item" data-label="Billing & Plans" onClick={() => navigate('/subscription')} style={{ color: '#94a3b8', cursor: 'pointer', marginBottom: '30px' }}><CreditCard size={24} /></div>
        <div className="sidebar-item mt-auto mb-3" data-label="Settings" onClick={() => navigate('/settings')} style={{ color: '#94a3b8', cursor: 'pointer', marginBottom: '20px' }}><Settings size={24} /></div>
        <div className="sidebar-item mb-3" data-label="Logout" onClick={onLogout} style={{cursor: 'pointer'}}><LogOut size={24} className="text-danger" /></div>
      </div>

     <div className="main-wrapper" style={{ marginLeft: '64px', width: 'calc(100% - 64px)' }}>
                  
                  
                  <header className="soc-header" style={{ height: '60px', borderBottom: '1px solid #1e293b', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 24px', background: '#0b0e14' }}>
                    <div className="d-flex align-items-center gap-3">
                 
                      <h5 
                        className="sidebar-item tooltip-bottom m-0 d-flex align-items-center"
                        data-label="Go to Landing Page"
                        onClick={() => window.location.href = 'https://noc.skynix.co'}
                        style={{ 
                          color: '#00f2ff', 
                          fontSize: '1.1rem', 
                          cursor: 'pointer',
                          lineHeight: '1',      
                          transform: 'translateY(1px)' 
                        }}
                      >
                        SKYNIX <span className="text-white ms-2">AI-NOC</span>
                      </h5>
                      <div style={{ width: '1px', height: '24px', background: '#334155' }}></div>
                      <div className="text-secondary small d-flex align-items-center gap-2" style={{ fontSize: '0.9rem', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        <Activity size={16} className="text-info" /> Security Operations Center
                      </div>
                    </div>
                    
                    <div className="d-flex gap-4 align-items-center">
                     <div style={{ 
                                                    background: data.subscription_status === 'active' 
                                                      ? 'rgba(0, 242, 255, 0.1)' 
                                                      : data.subscription_status === 'loading' 
                                                        ? 'rgba(148, 163, 184, 0.1)' 
                                                        : 'rgba(239, 68, 68, 0.1)',  
                                                    
                                                    color: data.subscription_status === 'active' 
                                                      ? '#00f2ff' 
                                                      : data.subscription_status === 'loading' 
                                                        ? '#94a3b8' 
                                                        : '#ef4444', 
                                                    
                                                    padding: '4px 14px', 
                                                    borderRadius: '20px', 
                                                    fontSize: '0.8rem', 
                                                    border: `1px solid ${
                                                      data.subscription_status === 'active' 
                                                        ? '#00f2ff44' 
                                                        : data.subscription_status === 'loading' 
                                                          ? '#94a3b844' 
                                                          : '#ef444444'
                                                    }`, 
                                                    fontWeight: '600' 
                                                  }}>
                                                    {data.subscription_status === 'loading' 
                                                      ? 'Verifying license...' 
                                                      : data.subscription_status === 'active'
                                                        ? `Plan: ${data.plan_name} (${data.agents_online}/${data.max_servers})`
                                                        : 'SUBSCRIPTION INACTIVE'
                                                    }
                                                  </div>
                                                  <div className="header-metric small text-secondary">
                                                <BrainCircuit size={14} className="me-1" style={{ color: '#a855f7' }}/> AI Tokens: <b className={data.ai_tokens_spent >= data.ai_tokens_limit ? "text-danger" : "text-white"}>
                                                    {data.ai_tokens_spent?.toLocaleString() || 0} / {data.ai_tokens_limit?.toLocaleString() || '1,000,000'}
                                                </b>
                                              </div>
                      <div style={{ background: 'rgba(16,185,129,0.1)', color: '#10b981', padding: '4px 14px', borderRadius: '20px', fontSize: '0.8rem', border: '1px solid #10b98144', fontWeight: '600' }}>
                         Core: Healthy
                      </div>
                      <div className="header-metric small text-secondary">
                         <Server size={14} className="me-1" style={{ color: '#00f2ff' }}/> Agents Online: <b className="text-white">{data.agents_online}</b>
                      </div>
                      <div className="header-metric small text-secondary">
                         <Wifi size={14} className="me-1" style={{ color: '#00f2ff' }}/> Uptime: <b className="text-white" style={{ color: '#00f2ff' }}>{data.uptime}</b>
                      </div>
                    </div>
                  </header>
                  
        <div className="p-4">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div style={{width: '250px'}}>
              <h2 className="fw-bold m-0">Raw Logs</h2>
              <p className="text-secondary small">Unfiltered stream of system events</p> 
            </div>
            
            <div className="d-flex align-items-center gap-3">
              <div className="d-flex bg-dark rounded-3 border border-secondary px-2 py-1 align-items-center" style={{height: '34px'}}>
                <Filter size={12} className="text-secondary me-2"/>
                <select value={hostFilter} onChange={(e) => setHostFilter(e.target.value)} className="bg-transparent text-info border-0 small fw-bold" style={{outline:'none', fontSize:'0.75rem', cursor:'pointer'}}>
                  {uniqueHosts.map(h => <option key={h} value={h} className="bg-dark">{h}</option>)}
                </select>
              </div>

              <div className="d-flex bg-dark p-1 rounded-3 border border-secondary">
                {['All', 'Info', 'Warning', 'Error'].map((f) => (
                  <button key={f} onClick={() => setActiveFilter(f)} className={`btn btn-sm px-3 border-0 transition-all ${activeFilter === f ? 'bg-info text-dark shadow-info' : 'text-secondary opacity-50'}`} style={{ fontSize: '0.75rem', fontWeight: '700', borderRadius: '4px' }}>
                    {f.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>

            <div className="d-flex gap-2" style={{width:'250px', justifyContent:'flex-end'}}>
              <button onClick={handleExport} className="btn btn-outline-secondary btn-sm text-white border-secondary px-3"><Download size={14}/> Export</button>
              <button onClick={() => fetchData(currentPage)} className="btn btn-info btn-sm fw-bold px-3 d-flex align-items-center gap-2" disabled={loading}>
                <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> {loading ? 'Wait...' : 'Refresh'}
              </button>
            </div>
          </div>

          <div className="noc-card p-0" style={{background: '#151921', borderRadius: '12px', border: '1px solid #1e293b'}}>
            <table className="table-noc m-0 w-100">
              <thead style={{background: 'rgba(0,0,0,0.2)'}}>
                <tr className="text-secondary small text-uppercase">
                  <th className="p-3 border-0">ID</th>
                  <th className="p-3 border-0">Source / Time</th>
                  <th className="p-3 border-0">Type / Service</th>
                  <th className="p-3 border-0 text-center">AI Link</th> 
                  <th className="p-3 border-0">Severity</th> 
                  <th className="p-3 border-0 text-end">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredLogs.map(log => (
                   <tr 
                      key={log.id} 
                      id={`row-${log.id}`} 
                      onClick={() => setSelectedLog(log)} 
                      style={{
                        borderBottom: '1px solid #1e293b', 
                        transition: '0.3s',
                        cursor: 'pointer', 
                        backgroundColor: selectedLog?.id === log.id ? 'rgba(0, 242, 255, 0.12)' : 'transparent',
                        borderLeft: selectedLog?.id === log.id ? '4px solid #00f2ff' : '4px solid transparent'
                      }}
                    >
                    <td className="p-3 text-info font-monospace fw-bold">RAW-{log.display_id || log.id}</td>
                    <td className="p-3">
                      <div className="fw-bold text-white small">{log.host}</div>
                      <div className="text-secondary mt-1" style={{fontSize: '11px'}}><Clock size={10} className="me-1"/>{log.last_seen}</div>
                    </td>
                    <td className="p-3">
                      <span className="text-white fw-bold py-1 px-2 rounded bg-dark border border-secondary border-opacity-25" style={{ fontSize: '10px' }}>
                        {log.type?.toUpperCase()}
                      </span>
                    </td>
                    <td className="p-3 text-center">
                      {log.incident_id ? (
                        <span className="badge d-flex align-items-center gap-1 mx-auto" style={{background: 'rgba(0, 242, 255, 0.1)', color: '#00f2ff', border: '1px solid #00f2ff44', fontSize:'10px', width: 'fit-content'}}>
                          <Sparkles size={10}/>  INC-{log.incident_display_id || log.incident_id}
                        </span>
                      ) : <span className="text-secondary opacity-25">—</span>}
                    </td>
                    <td className="p-3">
                      <span className={`badge ${log.severity === 1 ? 'badge-sev-1' : log.severity === 2 ? 'badge-sev-2' : 'badge-sev-3'}`}>
                        {log.severity === 1 ? 'CRITICAL' : log.severity === 2 ? 'WARNING' : 'INFO'}
                      </span>
                    </td>
                    <td className="p-3 text-end">
                      <button onClick={() => setSelectedLog(log)} className="btn btn-sm btn-outline-info py-1 px-2 fw-bold" style={{fontSize: '11px'}}><Eye size={14} className="me-1"/> Inspect</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          
          <div className="d-flex justify-content-center align-items-center gap-3 mt-4 pb-5">
            <button 
              disabled={currentPage === 1 || loading} 
              onClick={() => fetchData(currentPage - 1)}
              className="btn btn-sm btn-outline-secondary px-4"
            >
              Previous
            </button>
            <span className="text-secondary small fw-bold">
              Page <span className="text-info">{currentPage}</span> of {totalPages}
            </span>
            <button 
              disabled={currentPage === totalPages || loading} 
              onClick={() => fetchData(currentPage + 1)}
              className="btn btn-sm btn-outline-secondary px-4"
            >
              Next
            </button>
          </div>
        </div>
      </div>


    
      <div style={{ position: 'fixed', top: 0, right: selectedLog ? 0 : '-500px', width: '450px', height: '100vh', background: '#0d1117', boxShadow: '-10px 0 30px rgba(0,0,0,0.5)', transition: '0.4s ease-in-out', zIndex: 2000, borderLeft: '1px solid #1e293b', padding: '24px', overflowY: 'auto' }}>
        {selectedLog && (
          <div className="h-100 d-flex flex-column">
            <div className="d-flex justify-content-between align-items-start mb-4 pb-3 border-bottom border-secondary">
              <div>
                <div className="d-flex align-items-center gap-2 mb-1">
                   <div className="bg-info bg-opacity-10 p-2 rounded"><ScrollText size={20} className="text-info"/></div>
                   <h4 className="m-0 fw-bold text-white">RAW-{selectedLog.display_id || selectedLog.id}</h4>
                </div>
                <div className="text-secondary small">{selectedLog.type}</div>
              </div>
              <X size={24} className="text-secondary cursor-pointer" onClick={() => setSelectedLog(null)} />
            </div>

            <div className="row g-3 mb-4 text-start">
               <div className="col-6">
                  <label className="text-secondary small uppercase d-block mb-1 fw-bold" style={{fontSize:'10px'}}>Host Source</label>
                  <div className="small text-white fw-bold"><Server size={12} className="text-info me-1"/> {selectedLog.host}</div>
               </div>
               <div className="col-6">
                  <label className="text-secondary small uppercase d-block mb-1 fw-bold" style={{fontSize:'10px'}}>Timestamp</label>
                  <div className="small text-white"><Clock size={12} className="text-info me-1"/> {selectedLog.last_seen}</div>
               </div>
               <div className="col-12">
                  <label className="text-secondary small uppercase d-block mb-1 fw-bold" style={{fontSize:'10px'}}>Log Message / Title</label>
                  <div className="small text-info font-monospace p-2 bg-dark rounded border border-secondary border-opacity-25" style={{wordBreak: 'break-all'}}>{selectedLog.title}</div>
               </div>
            </div>

            <div className="flex-1 d-flex flex-column text-start">
               <label className="text-secondary small uppercase d-flex align-items-center gap-2 mb-2 fw-bold" style={{fontSize:'10px'}}><Activity size={14}/> Evidence Data</label>
               <div className="p-3 rounded border border-secondary bg-black overflow-auto" style={{maxHeight: '400px'}}>
                  <pre className="m-0" style={{color: '#7ee787', fontSize: '0.75rem', whiteSpace: 'pre-wrap'}}>
                    {JSON.stringify(selectedLog.evidence, null, 2)}
                  </pre>
               </div>
            </div>
          </div>
        )}
      </div>
      </SubscriptionGuard>
    </div>
  );
};

export default LogsPage;